import { api } from "@/lib/api";
import {
  ServiceTypeSetting,
  PaymentModeSetting,
  CustomerCategorySetting,
  CustomerTypeSetting,
} from "@/types";

export interface SettingsApiResponse<T> {
  success: boolean;
  data: T[];
  message?: string;
}

export interface SettingsApiSingleResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Service Types
export const serviceTypeService = {
  async getAll(): Promise<ServiceTypeSetting[]> {
    try {
      const response = await api.get<SettingsApiResponse<ServiceTypeSetting>>("/service-types");
      const data = Array.isArray(response.data) 
        ? response.data 
        : response.data.data || [];
      return data.map((item: any) => ({
        id: item.id,
        name: item.name,
        key: item.key,
        description: item.description,
        price: item.price,
        active: item.active !== false,
        createdAt: new Date(item.createdAt || item.created_at),
      }));
    } catch (error) {
      console.error("Error fetching service types:", error);
      throw error;
    }
  },

  async create(type: Omit<ServiceTypeSetting, "id" | "createdAt">): Promise<ServiceTypeSetting> {
    try {
      const response = await api.post<SettingsApiSingleResponse<ServiceTypeSetting>>(
        "/service-types",
        type
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error creating service type:", error);
      throw error;
    }
  },

  async update(
    id: string,
    type: Partial<ServiceTypeSetting>
  ): Promise<ServiceTypeSetting> {
    try {
      const response = await api.put<SettingsApiSingleResponse<ServiceTypeSetting>>(
        `/service-types/${id}`,
        type
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error updating service type:", error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/service-types/${id}`);
    } catch (error) {
      console.error("Error deleting service type:", error);
      throw error;
    }
  },
};

// Payment Modes
export const paymentModeService = {
  async getAll(): Promise<PaymentModeSetting[]> {
    // No backend endpoint exists yet for payment mode settings.
    return [];
  },

  async create(
    mode: Omit<PaymentModeSetting, "id" | "createdAt">
  ): Promise<PaymentModeSetting> {
    try {
      const response = await api.post<SettingsApiSingleResponse<PaymentModeSetting>>(
        "/payment-modes",
        mode
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error creating payment mode:", error);
      throw error;
    }
  },

  async update(
    id: string,
    mode: Partial<PaymentModeSetting>
  ): Promise<PaymentModeSetting> {
    try {
      const response = await api.put<SettingsApiSingleResponse<PaymentModeSetting>>(
        `/payment-modes/${id}`,
        mode
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error updating payment mode:", error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/payment-modes/${id}`);
    } catch (error) {
      console.error("Error deleting payment mode:", error);
      throw error;
    }
  },
};

// Customer Categories
export const customerCategoryService = {
  async getAll(): Promise<CustomerCategorySetting[]> {
    // No backend endpoint exists yet for customer category settings.
    return [];
  },

  async create(
    category: Omit<CustomerCategorySetting, "id" | "createdAt">
  ): Promise<CustomerCategorySetting> {
    try {
      const response = await api.post<SettingsApiSingleResponse<CustomerCategorySetting>>(
        "/customer-categories",
        category
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error creating customer category:", error);
      throw error;
    }
  },

  async update(
    id: string,
    category: Partial<CustomerCategorySetting>
  ): Promise<CustomerCategorySetting> {
    try {
      const response = await api.put<SettingsApiSingleResponse<CustomerCategorySetting>>(
        `/customer-categories/${id}`,
        category
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error updating customer category:", error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/customer-categories/${id}`);
    } catch (error) {
      console.error("Error deleting customer category:", error);
      throw error;
    }
  },
};

// Customer Types
export const customerTypeService = {
  async getAll(): Promise<CustomerTypeSetting[]> {
    // No backend endpoint exists yet for customer type settings.
    return [];
  },

  async create(
    type: Omit<CustomerTypeSetting, "id" | "createdAt">
  ): Promise<CustomerTypeSetting> {
    try {
      const response = await api.post<SettingsApiSingleResponse<CustomerTypeSetting>>(
        "/customer-types",
        type
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error creating customer type:", error);
      throw error;
    }
  },

  async update(
    id: string,
    type: Partial<CustomerTypeSetting>
  ): Promise<CustomerTypeSetting> {
    try {
      const response = await api.put<SettingsApiSingleResponse<CustomerTypeSetting>>(
        `/customer-types/${id}`,
        type
      );
      const data = response.data.data || response.data;
      return {
        ...data,
        createdAt: new Date(data.createdAt || (data as any).created_at),
      };
    } catch (error) {
      console.error("Error updating customer type:", error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/customer-types/${id}`);
    } catch (error) {
      console.error("Error deleting customer type:", error);
      throw error;
    }
  },
};

